import { useCallback, useContext, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import AuthContext from "@/contexts/AuthContext";

//Hooks
import { useFormHandler } from "@/core/hooks/useFormHandler";

//Services
import { AuthService } from "@/core/http/services/AuthService";
import { CategoriesService } from "@/core/http/services/CategoriesService";
import { dataValidateCreateUser } from "../utils/validator";
import { formatMobilePhoneWithDDD } from "../utils/phone";

const useRegister = () => {
  const navigate = useNavigate();
  const { signInWithToken } = useContext(AuthContext);

  const { handleChange, formData, setField, removeField } = useFormHandler();
  const validationErrorIdRef = useRef(0);

  const [loading, setLoading] = useState({
    topThree: false,
    matches: false,
  });

  const [error, setError] = useState({ type: null, message: null });
  const [validationError, setValidationError] = useState(null);
  const [requestError, setRequestError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [actionLoading, setActionLoading] = useState(false);

  const [categoriesData, setCategoriesData] = useState([]);

  const getCategories = useCallback(async () => {
    setLoading((prev) => ({ ...prev, topThree: true }));
    setError({ type: null, message: null });

    try {
      const { status, data, message } = await CategoriesService.get();

      if (data?.error || status !== 200) {
        setError({
          type: "topThree",
          message: message || "Erro ao buscar o top 3.",
        });
        return;
      }
      setCategoriesData(data || []);
    } catch (err) {
      setError({
        type: "topThree",
        message: err?.message || "Erro inesperado ao buscar o top 3",
      });
    } finally {
      setLoading((prev) => ({ ...prev, topThree: false }));
    }
  }, []);

  const raiseValidationError = (error, field) => {
    validationErrorIdRef.current += 1;
    setValidationError({
      id: validationErrorIdRef.current,
      error,
      field,
    });
  };

  const handleCreateUser = async (payload) => {
    setValidationError(null);
    setRequestError(null);
    setSuccessMessage(null);
    setActionLoading(true);

    const { validData, error, field } = dataValidateCreateUser(payload);

    if (!validData) {
      raiseValidationError(error, field);
      setActionLoading(false);
      return;
    }

    const normalizedPayload = {
      name: payload.name?.trim(),
      categoryId: payload.category?.id,
      email: payload.email?.trim(),
      phone: formatMobilePhoneWithDDD(payload.phone),
      password: payload.password,
      birthDate: payload.birthday,
    };

    try {
      const { status, message, data } = await AuthService.register(
        normalizedPayload,
      );

      if (status === 201 || status === 200) {
        if (!data?.token || !data?.user || !data?.player) {
          setRequestError("Conta criada, mas a resposta de autenticacao veio incompleta.");
          return;
        }

        await signInWithToken(data);

        setSuccessMessage("Conta criada com sucesso! Entrando na plataforma...");
        navigate("/user/redirect");
      } else {
        setRequestError(
          message || "Erro ao criar conta. Verifique os dados informados.",
        );
      }
    } catch (err) {
      setRequestError(err?.message || "Erro inesperado ao criar conta.");
    } finally {
      setActionLoading(false);
    }
  };

  const initialize = useCallback(async () => {
    await Promise.all([getCategories()]);
  }, [getCategories]);

  return {
    handleCreateUser,
    categoriesData,
    validationError,
    requestError,
    successMessage,
    actionLoading,

    error: error,

    loading,
    initialize,

    setField,
    removeField,
    handleChange,
    formData,
  };
};

export default useRegister;
